// supplies.js — non-bank supply purchases (Amazon, Costco, Restaurant
// Depot, …). Stores one row per ORDER, parses tolerant CSV input that
// either came from the canonical format (samples/amazon.csv) or from
// pasted Claude-Web extraction output following our prompt template.
//
// Public API
//   parseCsv(text)                  → { orders, warnings, headers }
//   dedupe(existing, parsed)        → { news, dupes }
//   computeSourceHash(order)        → 8-char fnv1a key for cross-source dedupe
//   summarize(rows, today=new Date()) → { totals, monthly, byCategory }
//   makeExtractionPrompt()          → string (the canonical Claude prompt)

const SUPPORTED_DATE_HEADERS    = ['order date', 'date', 'purchase date', 'ordered', 'invoice date'];
const SUPPORTED_REF_HEADERS     = ['order number', 'order id', 'order #', 'order', 'invoice', 'invoice number'];
const SUPPORTED_TOTAL_HEADERS   = ['order total', 'total', 'amount', 'grand total'];
const SUPPORTED_LINE_HEADERS    = ['line total', 'item total', 'subtotal'];
const SUPPORTED_DESC_HEADERS    = ['product', 'description', 'item', 'title', 'name'];
const SUPPORTED_QTY_HEADERS     = ['qty', 'quantity'];
const SUPPORTED_UNIT_HEADERS    = ['unit price', 'price', 'unit cost'];
const SUPPORTED_CATEGORY_HEADERS = ['category', 'department'];
const SUPPORTED_VENDOR_HEADERS  = ['vendor', 'seller', 'merchant', 'store'];

// ── CSV parsing ──────────────────────────────────────────────────────────────
// RFC-4180-lite; handles double-quoted cells with embedded commas/quotes/
// newlines. Returns array-of-arrays. Headers are NOT special here.
export function parseRawCsv(text) {
  const rows = [];
  let cell = '', row = [], inQuote = false;
  const s = String(text || '').replace(/\r\n?/g, '\n');
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuote) {
      if (c === '"' && s[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') inQuote = false;
      else cell += c;
    } else {
      if (c === '"') inQuote = true;
      else if (c === ',') { row.push(cell); cell = ''; }
      else if (c === '\n') { row.push(cell); rows.push(row); cell = ''; row = []; }
      else cell += c;
    }
  }
  if (cell.length || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((v) => v && v.trim().length));
}

// Locate a header column by checking each candidate name (case-insensitive).
// Returns the column index, or -1 when none match.
function findCol(headers, candidates) {
  const lc = headers.map((h) => String(h || '').toLowerCase().trim());
  for (const cand of candidates) {
    const idx = lc.indexOf(cand);
    if (idx >= 0) return idx;
  }
  return -1;
}

// Parse "$1,234.56", "1234.56", "$1,234", "(12.34)" → integer cents.
// Negatives in parens (accounting style) are honored. Returns null on
// unparseable input so the caller can flag the row.
export function parseMoneyToCents(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }
  s = s.replace(/[\s$]/g, '').replace(/,/g, '');
  if (s.startsWith('-')) { neg = true; s = s.slice(1); }
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) * (neg ? -1 : 1);
}

// ISO-ish date — accepts YYYY-MM-DD, MM/DD/YYYY, MM/DD/YY, M/D/YY, etc.
// Returns 'YYYY-MM-DD' or null when the input doesn't look like a date.
export function normalizeDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (!m) return null;
  let [, mo, d, y] = m;
  if (y.length === 2) y = (parseInt(y, 10) >= 70 ? '19' : '20') + y;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

// FNV-1a — used for stable source hashes when the source doesn't
// surface an order ref. Keeps dedupe boundaries crisp without needing
// crypto.subtle.
export function fnv1a(s) {
  let h = 0x811c9dc5 >>> 0;
  for (let i = 0; i < String(s).length; i++) {
    h ^= String(s).charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

export function computeSourceHash(order) {
  const parts = [
    (order.vendor || 'unknown').toLowerCase(),
    order.orderRef || '',
    order.orderDate || '',
    String(order.totalCents || 0),
  ];
  return fnv1a(parts.join('|'));
}

// ── Header-driven CSV → orders[] ─────────────────────────────────────────────
// Rows that share the same (orderRef, orderDate, vendor) collapse into a
// single order with items[]. When the source has no per-line columns,
// each row is its own one-item order.
export function parseCsv(text) {
  const raw = parseRawCsv(text);
  if (raw.length < 2) return { orders: [], warnings: ['empty or header-only CSV'], headers: raw[0] || [] };
  const headers = raw[0].map((h) => String(h || '').trim());
  const colDate   = findCol(headers, SUPPORTED_DATE_HEADERS);
  const colRef    = findCol(headers, SUPPORTED_REF_HEADERS);
  const colTotal  = findCol(headers, SUPPORTED_TOTAL_HEADERS);
  const colLine   = findCol(headers, SUPPORTED_LINE_HEADERS);
  const colDesc   = findCol(headers, SUPPORTED_DESC_HEADERS);
  const colQty    = findCol(headers, SUPPORTED_QTY_HEADERS);
  const colUnit   = findCol(headers, SUPPORTED_UNIT_HEADERS);
  const colCat    = findCol(headers, SUPPORTED_CATEGORY_HEADERS);
  const colVendor = findCol(headers, SUPPORTED_VENDOR_HEADERS);

  const warnings = [];
  if (colDate === -1)  warnings.push('No date column found; expected one of: ' + SUPPORTED_DATE_HEADERS.join(', '));
  if (colTotal === -1 && colLine === -1) warnings.push('No amount column found; expected one of: ' + [...SUPPORTED_TOTAL_HEADERS, ...SUPPORTED_LINE_HEADERS].join(', '));

  // Group rows by (vendor, orderRef, orderDate). When orderRef is empty
  // each row becomes its own order — common when the source is a
  // single-line receipt extraction.
  const orderMap = new Map();
  for (let i = 1; i < raw.length; i++) {
    const r = raw[i];
    const orderDate = colDate >= 0 ? normalizeDate(r[colDate]) : null;
    if (!orderDate) continue;
    const orderRef = colRef >= 0 ? String(r[colRef] || '').trim() : '';
    const vendor   = (colVendor >= 0 && r[colVendor]) ? String(r[colVendor]).trim() : 'Amazon';
    const key = orderRef ? `${vendor}|${orderRef}|${orderDate}` : `${vendor}|${orderDate}|row${i}`;
    let order = orderMap.get(key);
    if (!order) {
      order = {
        orderRef: orderRef || null,
        orderDate,
        vendor,
        totalCents: null,
        items: [],
        category: null,
      };
      orderMap.set(key, order);
    }
    // Order total: take the first non-null we see for this key (Amazon
    // duplicates Order Total on every line of the same order).
    if (order.totalCents == null && colTotal >= 0) {
      order.totalCents = parseMoneyToCents(r[colTotal]);
    }
    // Per-line item
    const desc = colDesc >= 0 ? String(r[colDesc] || '').trim() : '';
    const qty  = colQty  >= 0 ? Number(r[colQty]) || 1 : 1;
    const unit = colUnit >= 0 ? parseMoneyToCents(r[colUnit]) : null;
    const line = colLine >= 0 ? parseMoneyToCents(r[colLine]) : null;
    const cat  = colCat  >= 0 ? String(r[colCat] || '').trim() : null;
    if (desc || line != null) {
      order.items.push({
        description: desc,
        qty,
        unitCents: unit,
        lineCents: line != null ? line : (unit != null ? unit * qty : null),
        category:  cat || null,
      });
    }
    // Pick the most common item category as the order category for
    // headline grouping. (Tie-break: first seen.)
    if (cat && !order.category) order.category = cat;
  }

  // Finalize: derive total from line items when the source didn't have
  // an Order Total column.
  const orders = [];
  for (const o of orderMap.values()) {
    if (o.totalCents == null) {
      const sum = o.items.reduce((acc, it) => acc + (it.lineCents || 0), 0);
      o.totalCents = sum || null;
    }
    if (!o.totalCents) continue; // skip totally unpriced rows
    o.sourceHash = computeSourceHash(o);
    orders.push(o);
  }
  orders.sort((a, b) => (b.orderDate || '').localeCompare(a.orderDate || ''));
  return { orders, warnings, headers };
}

// ── Dedupe against the existing supplies table ───────────────────────────────
// Two-tier match: orderRef first (strongest), sourceHash second. Both
// are checked because a re-upload of the same CSV would otherwise
// produce two rows whenever orderRef was missing in the source.
export function dedupe(existingRows, parsedOrders) {
  const refSet = new Set();
  const hashSet = new Set();
  for (const r of existingRows || []) {
    if (r.orderRef) refSet.add((r.vendor || '').toLowerCase() + '|' + r.orderRef);
    if (r.sourceHash) hashSet.add(r.sourceHash);
  }
  const news = [], dupes = [];
  for (const o of parsedOrders) {
    const refKey = o.orderRef ? (o.vendor || '').toLowerCase() + '|' + o.orderRef : null;
    if ((refKey && refSet.has(refKey)) || hashSet.has(o.sourceHash)) {
      dupes.push(o);
    } else {
      news.push(o);
    }
  }
  return { news, dupes };
}

// ── Summarize for the report ─────────────────────────────────────────────────
export function summarize(rows, today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth();
  const q = Math.floor(m / 3);
  const ytdStart  = `${y}-01-01`;
  const qStart    = `${y}-${String(q * 3 + 1).padStart(2, '0')}-01`;
  const monthStart = `${y}-${String(m + 1).padStart(2, '0')}-01`;

  const totals = { ytd: 0, quarter: 0, month: 0, all: 0 };
  const byCategory = {};
  for (const r of rows || []) {
    const d = r.orderDate || '';
    const c = r.totalCents || 0;
    totals.all += c;
    if (d >= ytdStart)   totals.ytd     += c;
    if (d >= qStart)     totals.quarter += c;
    if (d >= monthStart) totals.month   += c;
    const cat = r.category || 'Uncategorized';
    byCategory[cat] = (byCategory[cat] || 0) + c;
  }

  // 12-month fixed spine — months newest-last (matches Reports.jsx chart).
  const months = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    months.push({ ym, total: 0 });
  }
  const byYm = new Map(months.map((mm) => [mm.ym, mm]));
  for (const r of rows || []) {
    const ym = (r.orderDate || '').slice(0, 7);
    const slot = byYm.get(ym);
    if (slot) slot.total += r.totalCents || 0;
  }
  return { totals, monthly: months, byCategory };
}

// ── The Claude-Web extraction prompt ─────────────────────────────────────────
// Copied to clipboard by the "Copy extraction prompt" button on the
// Supplies report. The user pastes it into Claude Web along with their
// Amazon order history page, a Costco receipt screenshot, etc., and
// Claude returns CSV in the canonical format that parseCsv understands.
export function makeExtractionPrompt() {
  return `You are extracting supply purchases for a restaurant's bookkeeping tool.

Output a single CSV block — nothing else, no commentary, no markdown
fence — using exactly these headers in this order:

  Order Date,Order Number,Vendor,Product,Qty,Unit Price,Line Total,Order Total,Category

Rules
- One row per LINE ITEM. If an order has 3 items, output 3 rows that
  share the same Order Date, Order Number, Vendor, and Order Total.
- Order Date: YYYY-MM-DD (e.g. 2026-05-17).
- Order Number: the merchant's reference (Amazon "111-2222222-3333333",
  Costco transaction id, invoice #). If genuinely missing, leave empty.
- Vendor: the merchant name (Amazon, Costco, Restaurant Depot, US Foods,
  Sysco, etc.). Use one consistent spelling per vendor in a single run.
- Product: the line item description, trimmed (no trailing model
  numbers or shipping IDs).
- Qty: integer; default 1.
- Unit Price, Line Total, Order Total: USD as "$X.XX" (with the $).
  Order Total includes tax + shipping for the whole order; Line Total
  is per-line pre-tax.
- Category: choose ONE of these labels per line (best fit):
    Food Ingredients · Beverages · Paper Goods · Cleaning Supplies ·
    Food Handling Supplies · Tableware · Smallwares · Equipment ·
    Office Supplies · Other

Drop returns, refunds, gift cards, and digital subscriptions — supply
purchases only.

Now extract from the source the user pasted below.`;
}

// ── Persistence helpers (Dexie wrappers) ─────────────────────────────────────
async function getDb() {
  const db = window.kanjo?.db;
  if (!db) throw new Error('kanjo.db not ready');
  return db;
}

export async function listAll(userId) {
  const db = await getDb();
  return db.supplies.where('userId').equals(userId).toArray();
}

export async function importOrders(userId, parsedOrders, sourceLabel) {
  if (!userId) throw new Error('userId required');
  const db = await getDb();
  const existing = await listAll(userId);
  const { news, dupes } = dedupe(existing, parsedOrders);
  const now = Date.now();
  const rows = news.map((o) => ({
    supplyId: 'sup-' + computeSourceHash(o),
    userId,
    orderRef:    o.orderRef,
    orderDate:   o.orderDate,
    vendor:      o.vendor,
    totalCents:  o.totalCents,
    items:       o.items || [],
    category:    o.category,
    sourceHash:  o.sourceHash,
    sourceLabel: sourceLabel || 'csv',
    importedAt:  now,
  }));
  if (rows.length) await db.supplies.bulkPut(rows);
  return { imported: rows.length, dupes: dupes.length };
}

export async function deleteOne(supplyId) {
  const db = await getDb();
  await db.supplies.delete(supplyId);
}
